export type AdLandingMode =
  | 'astro'
  | 'scriptures-and-sanskrit'
  | 'philosophy-and-vedanta'
  | 'yoga-meditation-ayurveda'
  | 'global-risk';

export const AD_LANDING_STORAGE_KEY = 'pendingAdMode';
export const AD_LANDING_EXPIRY_MS = 60 * 60 * 1000;

const LEGACY_ASTRO_STORAGE_KEY = 'pendingAstroMode';

const VALID_AD_MODES: AdLandingMode[] = [
  'astro',
  'scriptures-and-sanskrit',
  'philosophy-and-vedanta',
  'yoga-meditation-ayurveda',
  'global-risk',
];

export function isValidAdMode(value: unknown): value is AdLandingMode {
  return typeof value === 'string' && VALID_AD_MODES.includes(value as AdLandingMode);
}

function migrateLegacyAstroMode() {
  try {
    const legacySessionValue = sessionStorage.getItem(LEGACY_ASTRO_STORAGE_KEY);
    if (legacySessionValue === 'astro' && !sessionStorage.getItem(AD_LANDING_STORAGE_KEY)) {
      sessionStorage.setItem(AD_LANDING_STORAGE_KEY, 'astro');
    }
  } catch {}

  try {
    if (localStorage.getItem(AD_LANDING_STORAGE_KEY)) return;
    const raw = localStorage.getItem(LEGACY_ASTRO_STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw);
    if (parsed?.mode === 'astro' && typeof parsed?.expires === 'number' && parsed.expires > Date.now()) {
      localStorage.setItem(
        AD_LANDING_STORAGE_KEY,
        JSON.stringify({
          mode: 'astro',
          expires: parsed.expires,
        }),
      );
    }
  } catch {}
}

export function storePendingAdMode(mode: AdLandingMode): void {
  migrateLegacyAstroMode();

  try {
    sessionStorage.setItem(AD_LANDING_STORAGE_KEY, mode);
  } catch {}

  try {
    localStorage.setItem(
      AD_LANDING_STORAGE_KEY,
      JSON.stringify({
        mode,
        expires: Date.now() + AD_LANDING_EXPIRY_MS,
      }),
    );
  } catch {}
}

export function readPendingAdMode(): AdLandingMode | null {
  migrateLegacyAstroMode();

  try {
    const sessionValue = sessionStorage.getItem(AD_LANDING_STORAGE_KEY);
    if (isValidAdMode(sessionValue)) return sessionValue;
  } catch {}

  try {
    const raw = localStorage.getItem(AD_LANDING_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (isValidAdMode(parsed?.mode) && typeof parsed?.expires === 'number' && parsed.expires > Date.now()) {
      return parsed.mode;
    }

    localStorage.removeItem(AD_LANDING_STORAGE_KEY);
  } catch {
    try {
      localStorage.removeItem(AD_LANDING_STORAGE_KEY);
    } catch {}
  }

  return null;
}

export function clearPendingAdMode(): void {
  try {
    sessionStorage.removeItem(AD_LANDING_STORAGE_KEY);
  } catch {}

  try {
    localStorage.removeItem(AD_LANDING_STORAGE_KEY);
  } catch {}
}

export function getAdModeFromSearchParams(searchParams: URLSearchParams): AdLandingMode | null {
  const rawMode = searchParams.get('mode');
  return isValidAdMode(rawMode) ? rawMode : null;
}

export function getAdModeFromUrl(): AdLandingMode | null {
  if (typeof window === 'undefined') return null;
  return getAdModeFromSearchParams(new URLSearchParams(window.location.search));
}

export function getPostAuthAdDestination(mode: AdLandingMode): string {
  if (mode === 'astro') return '/chat?mode=astro';
  if (mode === 'global-risk') return '/global-risk';
  return '/chat';
}

export function buildAdIntentSearchParams(
  source: URLSearchParams | string | null | undefined,
): URLSearchParams {
  const input =
    typeof source === 'string'
      ? new URLSearchParams(source)
      : source instanceof URLSearchParams
        ? source
        : new URLSearchParams();

  const next = new URLSearchParams();
  const mode = getAdModeFromSearchParams(input);
  if (mode) {
    next.set('mode', mode);
  }

  input.forEach((value, key) => {
    if (/^utm_/i.test(key)) {
      next.set(key, value);
    }
  });

  return next;
}
